import React, { useEffect } from 'react';
import { Briefcase, Heart, Users, Star, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const CareersPage = () => {
  const { t, language } = useLanguage();

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    { 
      icon: Heart,
      title: { en: 'Competitive Salary', ar: 'راتب تنافسي', ku: 'مووچەی هاوسەنگ' }, 
      desc: { en: 'Industry-leading compensation packages', ar: 'حزم تعويضات رائدة في الصناعة', ku: 'پاکێجی قەرەبووی پێشەنگی پیشەسازی' } 
    },
    { 
      icon: Users,
      title: { en: 'Health Insurance', ar: 'التأمين الصحي', ku: 'بیمەی تەندروستی' }, 
      desc: { en: 'Comprehensive health coverage', ar: 'تغطية صحية شاملة', ku: 'داپۆشینی تەندروستی گشتگیر' } 
    },
    { 
      icon: Star,
      title: { en: 'Learning & Development', ar: 'التعلم والتطوير', ku: 'فێربوون و گەشەپێدان' }, 
      desc: { en: 'Continuous training and growth opportunities', ar: 'فرص التدريب والنمو المستمر', ku: 'ڕاهێنان و دەرفەتی گەشەی بەردەوام' } 
    },
    { 
      icon: Award,
      title: { en: 'Work-Life Balance', ar: 'التوازن بين العمل والحياة', ku: 'هاوسەنگی کار و ژیان' }, 
      desc: { en: 'Flexible schedules and paid time off', ar: 'جداول زمنية مرنة وإجازة مدفوعة', ku: 'خشتەی نەرم و پشووی کرێدراو' } 
    }
  ];

  const texts = {
    joinTeam: { en: 'JOIN OUR TEAM', ar: 'انضم لفريقنا', ku: 'بەشداربە لە تیمەکەمان' },
    noJobsTitle: { 
      en: 'No Job Openings Currently', 
      ar: 'لا توجد وظائف شاغرة حالياً', 
      ku: 'لە ئێستادا هیچ دەرفەتی کارکردن نییە' 
    },
    noJobsDesc: { 
      en: 'Thank you for your interest in joining our team. At the moment, we do not have any open positions. Please check back later or follow us on social media for updates on future opportunities.', 
      ar: 'شكراً لاهتمامك بالانضمام إلى فريقنا. في الوقت الحالي، ليس لدينا أي وظائف شاغرة. يرجى التحقق لاحقاً أو متابعتنا على وسائل التواصل الاجتماعي للحصول على تحديثات حول الفرص المستقبلية.', 
      ku: 'سوپاس بۆ هەڵبژاردنی دەروازەی گەرمیان. لە ئێستادا، هیچ دەرفەتی کارکردنمان نییە. تکایە دواتر سەردان بکەرەوە یان لە تۆڕە کۆمەڵایەتییەکان فۆڵۆومان بکە بۆ نوێکردنەوەکان لەسەر دەرفەتەکانی داهاتوو.' 
    },
    contactUs: { en: 'Contact Us', ar: 'تواصل معنا', ku: 'پەیوەندیمان پێوە بکە' },
    whyJoinUs: { en: 'WHY JOIN US', ar: 'لماذا تنضم إلينا', ku: 'بۆچی بەشداری لەگەڵمان بکەیت' },
    whyJoinUsTitle: { en: 'Why Work With Us?', ar: 'لماذا تعمل معنا؟', ku: 'بۆچی لەگەڵمان کار بکەیت؟' },
    whyJoinUsDesc: { 
      en: 'At Darwazay Garmyan, we believe in nurturing talent and providing an environment where every team member can grow and succeed.', 
      ar: 'في درواز كرميان، نؤمن برعاية المواهب وتوفير بيئة يمكن لكل عضو في الفريق النمو والنجاح فيها.', 
      ku: 'لە دەروازەی گەرمیان، باوەڕمان بە پەروەردەکردنی بەهرە هەیە و دابینکردنی ژینگەیەک کە هەموو ئەندامانی تیم بتوانن لێی گەشە بکەن و سەرکەوتوو بن.' 
    },
    stayConnected: { en: 'Stay Connected', ar: 'ابق على تواصل', ku: 'پەیوەندی بپارێزە' },
    stayConnectedDesc: { 
      en: 'Follow us on social media to be the first to know about new job opportunities.', 
      ar: 'تابعنا على وسائل التواصل الاجتماعي لتكون أول من يعرف عن فرص العمل الجديدة.', 
      ku: 'لە تۆڕە کۆمەڵایەتییەکان فۆڵۆومان بکە بۆ ئەوەی یەکەم کەس بیت کە دەرفەتی کارکردنی نوێ بزانی.' 
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1742112125567-3e8967bad60f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB0ZWFtfGVufDB8fHx8MTc2ODY4MDAzMnww&ixlib=rb-4.1.0&q=85"
          alt="Careers"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light animate-fade-in-up">
            {texts.joinTeam[language] || texts.joinTeam.en}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-in-up delay-200">
            {t('careers.title')}
          </h1>
          <p className="text-lg text-white/70 font-light animate-fade-in-up delay-300">{t('careers.subtitle')}</p>
        </div>
      </section>

      {/* No Jobs Available Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <div className="text-center scroll-reveal">
            <div className="w-24 h-24 mx-auto mb-8 bg-white/5 rounded-full flex items-center justify-center">
              <Briefcase className="w-12 h-12 text-white/30" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
              {texts.noJobsTitle[language] || texts.noJobsTitle.en}
            </h2>
            <p className="text-lg text-white/60 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
              {texts.noJobsDesc[language] || texts.noJobsDesc.en}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-xs uppercase tracking-[0.15em] font-medium hover:bg-white/90 transition-colors"
            >
              {texts.contactUs[language] || texts.contactUs.en}
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-16 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {texts.whyJoinUs[language] || texts.whyJoinUs.en}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
              {texts.whyJoinUsTitle[language] || texts.whyJoinUsTitle.en}
            </h2>
            <p className="text-white/60 font-light max-w-2xl mx-auto">
              {texts.whyJoinUsDesc[language] || texts.whyJoinUsDesc.en}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="scroll-reveal-scale border border-white/10 p-8 hover:border-white/30 transition-all duration-500 text-center"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 mx-auto mb-6 bg-white/5 rounded-full flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white/50" />
                  </div>
                  <h3 className="text-lg font-light text-white mb-4 tracking-wide">
                    {benefit.title[language] || benefit.title.en}
                  </h3>
                  <p className="text-white/50 font-light text-sm leading-relaxed">
                    {benefit.desc[language] || benefit.desc.en}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stay Connected Section */}
      <section className="py-24 bg-black border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6 scroll-reveal">
            {texts.stayConnected[language] || texts.stayConnected.en}
          </h2>
          <p className="text-white/60 font-light mb-10 max-w-2xl mx-auto scroll-reveal">
            {texts.stayConnectedDesc[language] || texts.stayConnectedDesc.en}
          </p>
          
          {/* Social Links */}
          <div className="flex justify-center gap-4 scroll-reveal">
            <a
              href="https://www.facebook.com/darwazaygarmyan"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:bg-white hover:text-black transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/darwazaygarmyan"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:bg-white hover:text-black transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/darwazaygarmyan"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:bg-white hover:text-black transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
